import React from 'react';
import type { GameTodayResult } from '../../types/games';
import { Card } from './ui';
import CopyRedemptionCode from './CopyRedemptionCode';

interface GameTodayResultCardProps {
  result: GameTodayResult;
  title?: string;
  subtitle?: string;
}

function resultHeadline(result: GameTodayResult): string {
  if (result.gameType === 'GOOGLE_REVIEW') {
    return result.resultSegment || 'Review reward';
  }
  if (result.gameType === 'BILL_SPLIT') {
    return `${result.resultSegment} picks up the tab`;
  }
  if (result.gameType === 'STOP_CLOCK' && result.isWin) {
    return result.resultSegment || 'Perfect timing!';
  }
  return result.resultSegment;
}

const GameTodayResultCard: React.FC<GameTodayResultCardProps> = ({ result, title, subtitle }) => {
  const isWin = result.isWin ?? Boolean(result.redemptionCode);
  const headline = resultHeadline(result);

  return (
    <Card
      style={{
        textAlign: 'center',
        border: isWin ? '1.5px solid var(--primary, #F5C21A)' : '1px solid var(--border-color, #E5E2D8)',
        marginTop: 16,
      }}
    >
      {title && (
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted, #9B8F6E)', letterSpacing: 0.5 }}>
          {title}
        </div>
      )}
      <div style={{ fontSize: isWin ? 34 : 28, marginTop: title ? 8 : 0 }}>{isWin ? '🥳' : '💛'}</div>
      <div
        style={{
          fontWeight: 800,
          fontSize: 20,
          color: isWin ? 'var(--primary-dark, #D4A416)' : 'var(--text-primary, #1F2428)',
          marginTop: 6,
        }}
      >
        {headline}
      </div>
      {subtitle && (
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary, #6B5E3E)', marginTop: 4 }}>{subtitle}</p>
      )}
      {!subtitle && isWin && result.gameType !== 'BILL_SPLIT' && (
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary, #6B5E3E)', marginTop: 4 }}>
          on your total bill today
        </p>
      )}
      {!isWin && result.gameType !== 'BILL_SPLIT' && (
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary, #6B5E3E)', marginTop: 6 }}>
          No reward this time — try again tomorrow.
        </p>
      )}
      {result.redemptionCode && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed var(--border-color, #E5E2D8)' }}>
          <div style={{ fontSize: 10.5, color: 'var(--text-muted, #9B8F6E)', letterSpacing: 1 }}>
            REDEMPTION CODE
          </div>
          <CopyRedemptionCode code={result.redemptionCode} />
          <div style={{ fontSize: 10.5, color: 'var(--text-muted, #9B8F6E)', marginTop: 4 }}>
            {result.redeemed
              ? 'Already redeemed'
              : result.gameType === 'GOOGLE_REVIEW'
                ? 'Show to staff when you request the bill'
                : 'Valid today only · show to waiter'}
          </div>
        </div>
      )}
      {result.gameType === 'GOOGLE_REVIEW' && result.userReviewLink && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--border-color, #E5E2D8)' }}>
          <div style={{ fontSize: 10.5, color: 'var(--text-muted, #9B8F6E)', letterSpacing: 1 }}>
            YOUR REVIEW LINK
          </div>
          <a
            href={result.userReviewLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              fontSize: 11.5,
              color: 'var(--text-secondary, #6B5E3E)',
              marginTop: 4,
              wordBreak: 'break-all',
            }}
          >
            {result.userReviewLink}
          </a>
        </div>
      )}
    </Card>
  );
};

export default GameTodayResultCard;
